import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $setSelection,
} from 'lexical';
import { AIIslandDock } from './AIIslandDock';
import { AIIslandDrawer } from './AIIslandDrawer';
import { AIIslandInput } from './AIIslandInput';
import { useAIIslandState } from './hooks/useAIIslandState';
import { useEditorSelection } from './hooks/useEditorSelection';
import './AIIsland.css';

const buildParagraphNodesFromText = (content) => {
  if (!content) {
    return [];
  }
  return String(content)
    .split(/\n/)
    .map((line) => {
      const paragraph = $createParagraphNode();
      if (line) {
        paragraph.append($createTextNode(line));
      }
      return paragraph;
    });
};

const restoreSelection = (selectionState) => {
  if (!selectionState) {
    return false;
  }
  const anchorNode = $getNodeByKey(selectionState.anchorKey);
  const focusNode = $getNodeByKey(selectionState.focusKey);
  if (!anchorNode || !focusNode) {
    return false;
  }
  const selection = $createRangeSelection();
  selection.anchor.set(anchorNode.getKey(), selectionState.anchorOffset, 'text');
  selection.focus.set(focusNode.getKey(), selectionState.focusOffset, 'text');
  $setSelection(selection);
  return true;
};

const buildMockResponse = ({ prompt, selection, actionType, t }) => {
  const normalizedPrompt = prompt.toLowerCase();
  const resolvedType = actionType
    || (normalizedPrompt.includes('table') ? 'table' : null)
    || (normalizedPrompt.includes('chart') || normalizedPrompt.includes('graph') ? 'chart' : null)
    || (normalizedPrompt.includes('data gaps') || normalizedPrompt.includes('review') ? 'review' : null)
    || (normalizedPrompt.includes('summarize') ? 'summarize' : null)
    || (normalizedPrompt.includes('rewrite') ? 'rewrite' : null)
    || (selection ? 'rewrite' : 'text');

  if (resolvedType === 'rewrite') {
    const original = selection?.text || prompt;
    const revised = selection?.text
      ? `${selection.text.trim()} (${t('reports.aiIsland.responses.rewriteSuffix')})`
      : `${prompt.trim()} (${t('reports.aiIsland.responses.rewriteSuffix')})`;
    return {
      type: 'rewrite',
      content: revised,
      diff: {
        original,
        revised,
      },
    };
  }

  if (resolvedType === 'summarize') {
    return {
      type: 'text',
      content: t('reports.aiIsland.responses.summary'),
    };
  }

  if (resolvedType === 'review') {
    return {
      type: 'text',
      content: t('reports.aiIsland.responses.reviewChecklist'),
    };
  }

  if (resolvedType === 'table') {
    return {
      type: 'table',
      content: t('reports.aiIsland.responses.table'),
    };
  }

  if (resolvedType === 'chart') {
    return {
      type: 'chart',
      content: t('reports.aiIsland.responses.chart'),
    };
  }

  return {
    type: 'text',
    content: t('reports.aiIsland.responses.default', { prompt }),
  };
};

export function AIIsland({ editor, expanded, onExpandedChange, onInsert, onReplace, onAddComment }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const responseTimeoutRef = useRef(null);
  const lastPromptRef = useRef('');
  const lastActionRef = useRef(null);
  const responseSelectionRef = useRef(null);
  const {
    isExpanded,
    setExpanded,
    drawerHeight,
    setDrawerHeight,
    mode,
    setMode,
    attachedSelection,
    setAttachedSelection,
    promptValue,
    setPromptValue,
    isStreaming,
    setIsStreaming,
    aiResponse,
    setAiResponse,
    pendingAction,
    setPendingAction,
    resetResponse,
  } = useAIIslandState({
    initialExpanded: false,
    expanded,
    onExpandedChange,
  });

  const selection = useEditorSelection(editor);

  useEffect(() => {
    setAttachedSelection(selection);
  }, [selection, setAttachedSelection]);

  useEffect(() => {
    return () => {
      if (responseTimeoutRef.current) {
        window.clearTimeout(responseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setExpanded(true);
        inputRef.current?.focus();
      }
      if (event.key === 'Escape') {
        if (isExpanded) {
          setExpanded(false);
        }
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, setExpanded]);

  const clearSelection = useCallback(() => {
    setAttachedSelection(null);
    if (!editor) {
      return;
    }
    editor.update(() => {
      const currentSelection = $getSelection();
      if ($isRangeSelection(currentSelection)) {
        currentSelection.collapseToEnd();
      }
    });
  }, [editor, setAttachedSelection]);

  const insertAtCursor = useCallback(
    (content) => {
      if (!content || !editor) {
        return;
      }
      editor.update(() => {
        const currentSelection = $getSelection();
        if ($isRangeSelection(currentSelection)) {
          const nodes = buildParagraphNodesFromText(content);
          currentSelection.insertNodes(nodes);
        }
      });
    },
    [editor]
  );

  const insertBelow = useCallback(
    (content) => {
      if (!content || !editor) {
        return;
      }
      editor.update(() => {
        const currentSelection = $getSelection();
        if ($isRangeSelection(currentSelection)) {
          const anchorNode = currentSelection.anchor.getNode();
          if (anchorNode.getType?.() === 'root') {
            return;
          }
          let topLevel = null;
          try {
            topLevel = anchorNode.getTopLevelElementOrThrow();
          } catch (error) {
            return;
          }
          const nodes = buildParagraphNodesFromText(content);
          if (!nodes.length) {
            return;
          }
          topLevel.insertAfter(nodes[0]);
          let previous = nodes[0];
          nodes.slice(1).forEach((node) => {
            previous.insertAfter(node);
            previous = node;
          });
        }
      });
    },
    [editor]
  );

  const replaceSelection = useCallback(
    (content) => {
      if (!content || !editor) {
        return;
      }
      editor.update(() => {
        if (responseSelectionRef.current) {
          restoreSelection(responseSelectionRef.current);
        }
        const currentSelection = $getSelection();
        if ($isRangeSelection(currentSelection)) {
          currentSelection.removeText();
          const nodes = buildParagraphNodesFromText(content);
          currentSelection.insertNodes(nodes);
        }
      });
    },
    [editor]
  );

  const handleSubmit = useCallback(
    (overridePrompt, options = {}) => {
      const resolvedPrompt = (overridePrompt ?? promptValue).trim();
      if (!resolvedPrompt) {
        return;
      }

      if (responseTimeoutRef.current) {
        window.clearTimeout(responseTimeoutRef.current);
      }

      lastPromptRef.current = resolvedPrompt;
      lastActionRef.current = options.actionType || null;
      responseSelectionRef.current = attachedSelection;

      setExpanded(true);
      setIsStreaming(true);
      setAiResponse(null);

      if (!options.keepPrompt) {
        setPromptValue('');
      }

      const response = buildMockResponse({
        prompt: resolvedPrompt,
        selection: attachedSelection,
        actionType: options.actionType,
        t,
      });

      responseTimeoutRef.current = window.setTimeout(() => {
        setAiResponse(response);
        setIsStreaming(false);
      }, 650);
    },
    [
      attachedSelection,
      promptValue,
      setAiResponse,
      setExpanded,
      setIsStreaming,
      setPromptValue,
      t,
    ]
  );

  const handleRegenerate = useCallback(() => {
    if (!lastPromptRef.current) {
      return;
    }
    handleSubmit(lastPromptRef.current, { keepPrompt: true, actionType: lastActionRef.current });
  }, [handleSubmit]);

  const handleCopy = useCallback(() => {
    if (!aiResponse?.content) {
      return;
    }
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(aiResponse.content).catch(() => {});
    }
  }, [aiResponse]);

  const handleInsertAtCursor = useCallback(() => {
    if (!aiResponse?.content) {
      return;
    }
    if (typeof onInsert === 'function') {
      onInsert(aiResponse.content, { placement: 'cursor', mode });
      return;
    }
    insertAtCursor(aiResponse.content);
  }, [aiResponse, insertAtCursor, mode, onInsert]);

  const handleInsertBelow = useCallback(() => {
    if (!aiResponse?.content) {
      return;
    }
    if (typeof onInsert === 'function') {
      onInsert(aiResponse.content, { placement: 'below', mode });
      return;
    }
    insertBelow(aiResponse.content);
  }, [aiResponse, insertBelow, mode, onInsert]);

  const handleReplace = useCallback(() => {
    if (!aiResponse?.content) {
      return;
    }
    if (typeof onReplace === 'function') {
      onReplace(aiResponse.content, responseSelectionRef.current);
      return;
    }
    replaceSelection(aiResponse.content);
  }, [aiResponse, onReplace, replaceSelection]);

  const handleAddComment = useCallback(() => {
    if (!aiResponse?.content) {
      return;
    }
    if (typeof onAddComment === 'function') {
      onAddComment(aiResponse.content);
    }
  }, [aiResponse, onAddComment]);

  const handleNewPrompt = useCallback(() => {
    if (responseTimeoutRef.current) {
      window.clearTimeout(responseTimeoutRef.current);
    }
    lastPromptRef.current = '';
    lastActionRef.current = null;
    setIsStreaming(false);
    resetResponse();
    setPromptValue('');
    inputRef.current?.focus();
  }, [resetResponse, setIsStreaming, setPromptValue]);

  useEffect(() => {
    if (!pendingAction || !aiResponse || isStreaming) {
      return;
    }
    if (pendingAction.type === 'replace') {
      handleReplace();
    } else if (pendingAction.type === 'insertBelow') {
      handleInsertBelow();
    } else if (pendingAction.type === 'insertAtCursor') {
      handleInsertAtCursor();
    }
    setPendingAction(null);
  }, [
    aiResponse,
    handleInsertAtCursor,
    handleInsertBelow,
    handleReplace,
    isStreaming,
    pendingAction,
    setPendingAction,
  ]);

  const modes = useMemo(
    () => [
      { id: 'auto', label: t('reports.aiIsland.modes.auto') },
      { id: 'ask', label: t('reports.aiIsland.modes.ask') },
      { id: 'edit', label: t('reports.aiIsland.modes.edit') },
      { id: 'review', label: t('reports.aiIsland.modes.review') },
    ],
    [t]
  );

  const responseLabels = useMemo(
    () => ({
      title: t('reports.aiIsland.responses.title'),
      empty: t('reports.aiIsland.responses.empty'),
      streaming: t('reports.aiIsland.responses.streaming'),
      diff: {
        title: t('reports.aiIsland.responses.diffTitle'),
        acceptAll: t('reports.aiIsland.responses.acceptAll'),
      },
      actions: {
        copy: t('reports.aiIsland.actions.copy'),
        regenerate: t('reports.aiIsland.actions.regenerate'),
        replace: t('reports.aiIsland.actions.replaceSelection'),
        insertAtCursor: t('reports.aiIsland.actions.insertAtCursor'),
        insertBelow: t('reports.aiIsland.actions.insertBelow'),
        addComment: t('reports.aiIsland.actions.addComment'),
      },
    }),
    [t]
  );

  const inputPlaceholder = t('reports.aiIsland.placeholder');

  const drawerActions = useMemo(
    () => ({
      copy: handleCopy,
      regenerate: handleRegenerate,
      replace: handleReplace,
      insertBelow: handleInsertBelow,
      insertAtCursor: handleInsertAtCursor,
      addComment: typeof onAddComment === 'function' ? handleAddComment : null,
      acceptAll: handleReplace,
      acceptChunk: handleReplace,
    }),
    [
      handleAddComment,
      handleCopy,
      handleInsertAtCursor,
      handleInsertBelow,
      handleRegenerate,
      handleReplace,
      onAddComment,
    ]
  );
  const emptyStateTasks = useMemo(
    () => [
      {
        id: 'rewrite-executive',
        label: t('reports.aiIsland.empty.tasks.rewrite'),
        prompt: t('reports.aiIsland.prompts.rewrite'),
        actionType: 'rewrite',
        autoApply: true,
        requiresSelection: true,
        applyTarget: 'replace',
      },
      {
        id: 'key-findings',
        label: t('reports.aiIsland.empty.tasks.summarize'),
        prompt: t('reports.aiIsland.prompts.summarize'),
        actionType: 'summarize',
        autoApply: true,
        requiresSelection: false,
        applyTarget: 'insertBelow',
      },
      {
        id: 'metrics-table',
        label: t('reports.aiIsland.empty.tasks.table'),
        prompt: t('reports.aiIsland.prompts.table'),
        actionType: 'table',
        autoApply: true,
        requiresSelection: false,
        applyTarget: 'insertBelow',
      },
      {
        id: 'data-gaps',
        label: t('reports.aiIsland.empty.tasks.review'),
        prompt: t('reports.aiIsland.prompts.review'),
        actionType: 'review',
        autoApply: true,
        requiresSelection: false,
        applyTarget: 'insertBelow',
      },
    ],
    [t]
  );

  const emptyState = useMemo(
    () => ({
      selectionLabel: t('reports.aiIsland.empty.selectionLabel'),
      tasksTitle: t('reports.aiIsland.empty.tasksTitle'),
      tasks: emptyStateTasks,
    }),
    [emptyStateTasks, t]
  );

  const handleTaskSelect = useCallback(
    (task) => {
      const resolvedTask = typeof task === 'string' ? { label: task } : task;
      const nextValue = resolvedTask?.prompt || resolvedTask?.label;
      if (!resolvedTask || !nextValue) {
        return;
      }
      const requiresSelection = Boolean(resolvedTask.requiresSelection);
      const hasSelection = Boolean(attachedSelection?.text);
      const canAutoApply = Boolean(resolvedTask.autoApply && (!requiresSelection || hasSelection));

      setPromptValue(nextValue);
      inputRef.current?.focus();

      if (!canAutoApply) {
        setPendingAction(null);
        return;
      }

      setPendingAction({ type: resolvedTask.applyTarget || 'insertBelow' });
      handleSubmit(nextValue, { actionType: resolvedTask.actionType });
    },
    [attachedSelection, handleSubmit, setPendingAction, setPromptValue]
  );

  return (
    <div className="ai-island" data-expanded={isExpanded}>
      {isExpanded ? (
        <AIIslandDrawer
          height={drawerHeight}
          onHeightChange={setDrawerHeight}
          onCollapse={() => setExpanded(false)}
          mode={mode}
          onModeChange={setMode}
          response={aiResponse}
          isStreaming={isStreaming}
          actions={drawerActions}
          modes={modes}
          responseLabels={responseLabels}
          promptValue={promptValue}
          emptyState={emptyState}
          selection={attachedSelection}
          onTaskSelect={handleTaskSelect}
          onNewPrompt={handleNewPrompt}
          newPromptLabel={t('reports.aiIsland.actions.newPrompt')}
        >
          <AIIslandInput
            value={promptValue}
            onChange={setPromptValue}
            onSubmit={() => handleSubmit()}
            onFocus={() => setExpanded(true)}
            placeholder={inputPlaceholder}
            inputRef={inputRef}
            shortcutLabel="Cmd+Enter"
          />
        </AIIslandDrawer>
      ) : (
        <AIIslandDock
          onExpand={() => setExpanded(true)}
          attachedSelection={attachedSelection}
          onClearSelection={clearSelection}
          promptValue={promptValue}
          onPromptChange={setPromptValue}
          onSubmit={() => handleSubmit()}
          placeholder={inputPlaceholder}
          selectionLabel={t('reports.aiIsland.selectionAttached')}
          clearSelectionLabel={t('reports.aiIsland.clearSelection')}
          shortcutLabel="Cmd+K"
          inputRef={inputRef}
        />
      )}
    </div>
  );
}
